/**
 * @fileoverview Instructor user guide — searchable, organized, and accessible
 * from the public site. Designed so instructors can find any answer in 3 clicks
 * or less. The searchable UI lives in `components/GuideView`; this file only
 * supplies the instructor-facing content. Students/parents have their own guide
 * (StudentGuidePage) and organization admins theirs (OrgGuidePage), so neither
 * portal ever surfaces instructor-only flows.
 *
 * Scope rule: this guide documents ONLY what a production instructor can
 * actually do. A reader hunting for a nav item that isn't there loses more
 * trust than an undocumented feature costs. Three live examples of the rule:
 *
 *   - Practice ASSIGNMENTS ship dark in production (REACT_APP_ASSIGNMENTS_ENABLED
 *     is QA-only in .github/workflows/deploy-frontend.yml) and are absent here.
 *   - Bulk student IMPORT has a page but no route and no endpoint (see the
 *     MOCKUP note in StudentImportPage) — the org-side import is the real one,
 *     and it is documented in the ORG guide, not this one.
 *   - Hans-native slot authoring, the "Add to calendar" ICS link and the Hans
 *     booking page all live on SchedulePage, which production no longer mounts
 *     (SCHEDULING_ENABLED && SCHEDULER_VIEW_ENABLED picks /calendar instead).
 *     Scheduling is documented as what it now is: a mirror of Kalver Scheduler.
 *
 * Opened from the iOS app (`?client=ios`), the guide must not tell the reader
 * to buy, upgrade or add minutes on the web: a section that does carries an
 * `ios` override (or `webOnly`), applied by `lib/guideClient` and pinned by
 * `GuidePage.test.jsx`.
 *
 * Plans are described but never NAMED, and no price is quoted. The pricing page
 * states both once, authoritatively; a guide that repeated them would be one
 * more place every rename and reprice has to be chased — pinned by
 * `GuidePage.test.jsx`.
 *
 * Screenshots and walkthrough GIFs live in `public/academy/` and are captured
 * from the real instructor UI by `scripts/capture-academy.mjs`. Re-run it after
 * a UI change rather than editing images by hand.
 */

import {
  Rocket,
  Mic,
  ShieldCheck,
  Users,
  FileText,
  CalendarDays,
  Building2,
  CreditCard,
  BookOpen,
  GraduationCap,
  Settings,
  HelpCircle,
} from "lucide-react";
import GuideView from "@/components/GuideView";
import { supportEmail } from "@/lib/companyBrand";

/** Shorthand for an image in `public/academy`. */
const shot = (file, alt, caption, layout) => ({
  src: `/academy/${file}`,
  alt,
  caption,
  kind: file.endsWith(".gif") ? "gif" : "image",
  ...(layout ? { layout } : {}),
});

/* ------------------------------------------------------------------ */
/*  Guide content data                                                 */
/* ------------------------------------------------------------------ */

const guideCategories = [
  {
    id: "getting-started",
    icon: Rocket,
    title: "Getting Started",
    sections: [
      {
        id: "what-is-hans",
        title: "What is Hans?",
        content:
          "Hans is your lesson assistant. It captures the lesson and drafts a summary with practice items, key concepts, and progress notes. You review and edit the draft, then share it with the student or their parent. Around that it keeps your teaching week, your roster, the programs you teach in, and a portal where your students follow along.",
      },
      {
        id: "first-steps",
        title: "Setting up your account",
        steps: [
          "Sign up with Google, Apple, or an email address and password",
          "If you signed up with email, enter the 6-digit code we send you — Google and Apple sign-ups skip this step",
          "Add your name and the instruments you teach",
          "Look for the sample student on your roster — it's already set up so you can try a recording end to end before adding anyone real",
          "Add your first student, ask for recording consent, and record a lesson",
        ],
        note: "The sample student exists so you can see a full lesson summary without waiting for a real lesson. Delete it whenever you like.",
      },
      {
        id: "navigation",
        title: "Finding your way around",
        content:
          "Everything lives in the left sidebar, with the Record lesson button pinned at the top so you can start a lesson from anywhere:",
        items: [
          { label: "Home", desc: "Today's lessons, recent activity, and quick links" },
          { label: "Students", desc: "Your roster, student profiles, and classes" },
          { label: "Lessons", desc: "Everything you've taught, with totals and a spreadsheet export" },
          { label: "Programs", desc: "The programs you teach in, open opportunities, and your invitations" },
          { label: "Calendar", desc: "Your teaching week, mirrored from Kalver Scheduler" },
          { label: "Invoice history", desc: "Invoices raised for your students" },
          { label: "Repertoires", desc: "Teaching guides for any piece, saved to your library" },
          { label: "Tools", desc: "Metronome and chromatic tuner" },
          { label: "Settings", desc: "Profile, scheduling, plan, notifications, and help" },
        ],
        media: shot("home.png", "The Hans dashboard showing today's lessons, roster counts, and recent lessons", "Home"),
      },
      {
        id: "apps",
        title: "On your phone, and in the dark",
        content:
          "Hans runs in any browser and as an iOS app. The app records in the background, which a browser on an iPhone cannot — if you teach from a phone, it's the better tool. Both clients ship a light and a dark appearance; switch under Settings → Account → Appearance. The choice is saved on that device, so your laptop and your phone can differ.",
      },
    ],
  },
  {
    id: "recording",
    icon: Mic,
    title: "Recording Lessons",
    sections: [
      {
        id: "how-to-record",
        title: "Recording a lesson",
        content:
          "Press Record lesson in the sidebar, pick who it's for, and teach. Hans handles the rest.",
        steps: [
          'Press "Record lesson" in the sidebar',
          "Pick the student — if they have a lesson scheduled today, they're offered at the top and the recording is linked to that time slot automatically",
          "Add a lesson title if you want one (optional)",
          'Press "Start Recording" and teach normally — there is nothing to manage during the lesson',
          'Press stop when you\'re done, then confirm',
        ],
        note: "Recordings must be at least 10 seconds and stop automatically at 2 hours. Keep the device in the room, within a few feet if you can.",
        media: shot(
          "record-a-lesson.gif",
          "Choosing a student, naming the lesson, and starting a recording in Hans",
          "Pick a student, name it, record"
        ),
      },
      {
        id: "processing",
        title: "What happens after you stop",
        content:
          "The audio uploads, then Hans transcribes it, reads the transcript, and writes the summary. You'll see it appear on the lesson page when it's ready — usually in a few minutes, longer for a long lesson.",
        note:
          "Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days.",
      },
      {
        id: "recording-minutes",
        title: "Recording minutes",
        content:
          "AI recording is metered in minutes. Settings → Plan & billing shows what your plan includes, what's left, and when it renews; if you run short you can buy a one-off pack of minutes from the same place. Minutes are counted per plan, and if an organization seats you, the minutes you use inside its programs are counted against the organization rather than you.",
        ios: {
          content:
            "AI recording is metered in minutes. Settings in the app shows what your plan includes, what's left, and when it renews. Minutes are counted per plan. If an organization seats you, the minutes you use in its programs count against the organization, not you.",
        },
      },
      {
        id: "recording-on-iphone",
        title: "Recording on an iPhone or iPad",
        content:
          "If you record in Safari or Chrome on an iPhone or iPad, keep the Hans tab open and the screen awake. iOS stops giving the browser microphone access the moment you switch apps or the screen locks, and the recording ends there.",
        items: [
          { label: "Stay in the tab", desc: "Switching apps or locking the screen ends the recording" },
          { label: "Hans tells you", desc: "You'll see this warning before you start, not after" },
          { label: "Nothing is lost", desc: "If the microphone does drop out, what was captured up to that point is kept for you to upload" },
        ],
        note: "The Hans iOS app records in the background and isn't affected by this. If you teach from a phone regularly, the app is the better tool.",
      },
      {
        id: "upload-existing-audio",
        title: "Uploading audio you recorded elsewhere",
        content:
          'If you recorded a lesson on Voice Memos, Zoom, or any other recorder, you don\'t have to redo it. On the recording screen choose "Upload a recording", pick the student, and select the audio file. Hans processes it exactly like a lesson recorded in the app.',
      },
      {
        id: "re-record",
        title: "Re-recording a lesson",
        content:
          'If a recording didn\'t capture what you needed — the microphone was covered, the room was too loud, the wrong student was selected — open the lesson and choose "Re-record". Hans records again and replaces the summary on that same lesson, so you don\'t end up with a duplicate in the student\'s history.',
      },
      {
        id: "failed-uploads",
        title: "If an upload doesn't go through",
        content:
          "A recording is saved in your browser before the upload starts, so a dropped connection or a closed tab never loses a lesson. A take that couldn't be sent waits on the Saved recordings page (Home and Lessons show how many), each with the reason and the one thing that moves it — Try again, Request consent, Plan & usage, or Review notice. They survive a refresh — come back later and they'll still be waiting.",
      },
      {
        id: "recording-tips",
        title: "Getting the best summaries",
        items: [
          { label: "Quiet room", desc: "Background noise is the single biggest factor in transcription quality" },
          { label: "Keep the device close", desc: "Within a few feet of where you and the student are talking" },
          { label: "Talk normally", desc: "No need to over-enunciate or narrate for the microphone" },
          { label: "Say what you're working on", desc: "Naming pieces, bar numbers, and goals out loud gives Hans more to write from" },
          { label: "Long playing is fine", desc: "Stretches of music without talking won't confuse the summary" },
        ],
      },
    ],
  },
  {
    id: "consent",
    icon: ShieldCheck,
    title: "Recording Consent",
    sections: [
      {
        id: "why-consent",
        title: "Why consent comes first",
        content:
          "Hans won't record a student until consent has been given. This isn't optional and it isn't a setting you can turn off — recording a child without a parent's permission is exactly the thing the law is written about, and the gate protects you as much as the family. Consent is never sent automatically: you decide when to ask.",
      },
      {
        id: "send-consent",
        title: "Asking for consent",
        content:
          "You send the request from the student's profile. The recording screen only says where consent stands — it holds Record until the family has answered.",
        steps: [
          "Make sure the student's age group and email address are set on their profile",
          'Press "Request consent" on the profile',
          "Hans emails the full consent form to the right person for that age group",
          "They accept with one tap; the student unlocks for recording immediately",
          'Nothing back after a day or two? Press "Resend" on the profile to send a fresh link',
        ],
        media: shot(
          "send-consent.gif",
          "The recording screen showing a consent-pending banner with resend options for a student who hasn't consented yet",
          "Consent blocks recording — and offers the fix in place"
        ),
      },
      {
        id: "age-bands",
        title: "Age groups, and who gets asked",
        content:
          "The age group you set on each student decides who is asked for consent and who receives the lesson summaries afterwards:",
        items: [
          { label: "Adult", desc: "The student consents for themselves and receives their own summaries" },
          { label: "13–17", desc: "A parent or guardian consents; summaries go to the parent, and to the teen as well unless the parent opts out" },
          { label: "Under 13", desc: "A parent consents and receives an extra confirmation email with a cancel link; summaries go to the parent only, never to the child" },
        ],
        note: "Set the age group before you ask. It's what determines which address the request is sent to.",
      },
      {
        id: "offline-consent",
        title: "Consent collected on paper",
        content:
          'If a family signed a paper form, you can record that instead of asking again. On the student\'s profile choose "Mark as consented (offline)" and confirm that you actually hold a signed form — it\'s logged against your name and the time you did it. For an under-13 student, the parent still gets a confirmation email so they keep an independent way to cancel.',
      },
      {
        id: "consent-at-booking",
        title: "Consent given when a family books",
        content:
          "If your booking runs through Kalver Scheduler, families can answer the consent question as they book — for themselves, or as a parent or guardian, with a separate choice per attendee. Turn it on under Settings → Scheduling. It is the same consent, recorded the same way: a booking on its own is never proof of it.",
      },
      {
        id: "revoking",
        title: "If someone changes their mind",
        content:
          "Every summary email carries a link to withdraw consent. If it's used, recording locks for that student, any audio still on our side is deleted, and you're notified. Summaries already written are kept — those are your teaching records. You can ask again later, and a fresh request sends a new link.",
      },
      {
        id: "consent-ledger",
        title: "Your consent record",
        content:
          "Settings → Consent ledger lists every request, grant, decline, and withdrawal across your whole studio, with dates. You can export it as a CSV. If anyone ever asks you to show that consent was obtained, this is the answer.",
      },
    ],
  },
  {
    id: "students",
    icon: Users,
    title: "Students & Classes",
    sections: [
      {
        id: "add-student",
        title: "Adding a student",
        content:
          'Students → "Add student". Name and email are what matter most — the email is where consent requests, summaries, and portal invitations go. For anyone under 18, add the parent or guardian\'s name and email too, and set the age group.',
        note: "A student's email has to be unique on your roster, but the same person can be on several teachers' rosters without any conflict.",
        media: shot("roster.png", "The Hans student roster listing students with instrument and consent status", "Students"),
      },
      {
        id: "roster-tabs",
        title: "Finding people on a big roster",
        content:
          "The roster splits in two ways. Students and Classes are the top-level tabs; inside Students, three more narrow the list:",
        items: [
          { label: "All students", desc: "Everyone on your working roster" },
          { label: "Lessons", desc: "The students you teach one to one" },
          { label: "Class students", desc: "People who arrived as part of a class roster" },
          { label: "Program tags", desc: "A chip on each row naming the program a student is taught in — switch them off if they're noise" },
        ],
      },
      {
        id: "student-profile",
        title: "The student profile",
        content: "Everything about one student in one place:",
        items: [
          { label: "Contacts", desc: "Who is billed, who is emailed, and who consented" },
          { label: "Lesson history", desc: "Every summary, newest first" },
          { label: "Goals", desc: "What the practising is for, moved along a four-step stage as they get there" },
          { label: "Consent", desc: "Current status, with resend and offline options" },
          { label: "Notes", desc: "Private to you — never shown to the student or parent" },
        ],
        media: shot(
          "student-profile.png",
          "A Hans student profile showing contacts, consent status, goals, lesson history, and instructor notes",
          "Student profile"
        ),
      },
      {
        id: "archiving",
        title: "Archiving a student who has stopped",
        content:
          "When someone finishes lessons, archive them rather than deleting them. An archived student keeps every lesson, summary and consent record, drops off the working roster, and no longer counts against a plan's active-student limit. You can bring them back at any time. Deleting is permanent and is almost never what you want.",
      },
      {
        id: "classes",
        title: "Group lessons and classes",
        content:
          "For an ensemble, studio class, or cohort, create a class from the Classes tab on your roster and add members. Recording a class produces one shared summary that goes to every member individually, and edits apply to the whole class at once.",
        note: "Every member needs consent before a class recording can start — the banner tells you exactly who's outstanding and lets you chase them in one go.",
      },
      {
        id: "class-sessions",
        title: "Every day a class meets",
        content:
          "A class that meets twenty times is twenty teaching records, and the question week after week is which ones you haven't written up. A class's Sessions page answers that first: every meeting it has, past and upcoming, with the ones missing notes carrying the only action on the page. Cancelled meetings stay listed — a snow day is part of the record — and never count as owed.",
        media: shot(
          "class-sessions.png",
          "The sessions page for a class in Hans, listing every meeting with the ones still missing notes",
          "A class's meetings"
        ),
      },
      {
        id: "portal-invite",
        title: "Inviting a student to their portal",
        content:
          'Open the student\'s profile and press "Invite". Hans emails them a link to set a password and sign in. From then on they can read their own lesson summaries and track their practice.',
        note:
          "The invitation always goes to the student's own email address, whatever their age — that address becomes their login. Make sure it's right before you send.",
        media: shot(
          "invite-to-portal.gif",
          "Sending a student portal invitation from a student profile in Hans",
          "Invite a student to the portal"
        ),
      },
    ],
  },
  {
    id: "lesson-summaries",
    icon: FileText,
    title: "Lesson Summaries",
    sections: [
      {
        id: "reading-summary",
        title: "What's in a summary",
        content:
          "Every summary is written from what actually happened in the lesson, in the order you read it:",
        items: [
          { label: "Summary", desc: "What the lesson covered, in a short paragraph" },
          { label: "Student focus", desc: "What this student is working toward, and anything they asked for" },
          { label: "Practice this week", desc: "Specific things for them to do before next time" },
          { label: "Instructor notes", desc: "Anything you add in your own words — the family reads it as \"Notes from your teacher\"" },
          { label: "Core concepts", desc: "The underlying skills behind the work" },
        ],
        note: "A lesson recorded before Student focus existed still shows its old Key elements section where it always sat — nothing was rewritten underneath you.",
      },
      {
        id: "editing",
        title: "Edit before you send",
        content:
          'Hans writes a first draft; you\'re the teacher. Press "Edit" to open the whole summary — title, overview, and every list — then fix, add, or delete anything. Save once and it\'s all updated.',
        steps: [
          'Open the lesson and press "Edit"',
          "Change any text; use the add and remove controls on list items",
          "Press Save",
          'Then send it with "Send to Student"',
        ],
        note: "Nothing is sent to anyone until you press send. Read the draft first — it's your name on it.",
        media: shot(
          "lesson-summary.gif",
          "A Hans lesson summary, then the same summary in edit mode with every field editable",
          "Review, then edit anything"
        ),
      },
      {
        id: "sending",
        title: "Sending the summary",
        content:
          '"Send to Student" emails a formatted version with the practice items highlighted. Who receives it follows the student\'s age group: adults get their own, teens and their parents both get it, and for under-13s it goes to the parent alone. The recipients are listed on the lesson page before you send, so you always know where it\'s going.',
      },
      {
        id: "export-pdf",
        title: "Saving a PDF",
        content:
          '"Export PDF" produces a clean, printable copy of the summary — useful for a recital file, a studio record, or a parent who prefers paper.',
      },
      {
        id: "manual-lessons",
        title: "Writing up a lesson you didn't record",
        content:
          'You don\'t need audio to keep a record. On the recording screen choose "Write notes instead" and type the lesson up yourself. It gets the same summary page, the same editing, and the same send-by-email — it just skips the AI step.',
        note: "Useful for a lesson you forgot to record, a phone check-in, or a student who hasn't consented to recording yet.",
      },
      {
        id: "lesson-history",
        title: "Everything you have taught",
        content:
          "The Lessons page is the whole history in one place: filter it by period or kind, read the totals it works out as you do — lessons taught, hours at the instrument, your weekly average — and see at a glance which summaries reached the family and which didn't. Export the result as a spreadsheet for taxes, a school report, or your own records. A single student's history also sits on their profile, and recent lessons on Home.",
        media: shot(
          "lessons.png",
          "The Hans Lessons page: a month-grouped lesson history beside teaching totals and charts",
          "Lessons"
        ),
      },
      {
        id: "notes-owed",
        title: "The lessons you haven't written up",
        content:
          "Notes owed lists every past session with no notes, newest first — the one you can still remember is the one worth writing up. It's reached from the banner on your Calendar and from a line on the Lessons page. Cancelled sessions never appear; nothing was taught, so nothing is owed.",
        note: "It is deliberately not a filter on Lessons: a session with no notes is a lesson that does not exist yet, and you cannot filter a list for records nobody wrote.",
        media: shot(
          "notes-owed.png",
          "The notes owed page in Hans, listing past sessions that still need writing up",
          "Notes owed"
        ),
      },
    ],
  },
  {
    id: "scheduling",
    icon: CalendarDays,
    title: "Your Teaching Week",
    sections: [
      {
        id: "calendar",
        title: "The Calendar tab",
        content:
          "Calendar shows your teaching week: who you are teaching, when, and which program it belongs to. Lessons you've already written up are marked, so a glance tells you what still needs notes. Home shows the same day's lessons, and the recording screen offers today's students first.",
        items: [
          { label: "List", desc: "The week down the page, with the most detail" },
          { label: "Week", desc: "The same week running left to right" },
          { label: "Month", desc: "Less per session, so a whole term fits" },
        ],
        media: shot(
          "calendar-week.png",
          "The Hans calendar showing a teaching week laid out left to right with each lesson and its program",
          "Your week"
        ),
      },
      {
        id: "scheduler-owns-it",
        title: "Where lessons are actually scheduled",
        content:
          "Kalver Scheduler holds your lessons, classes and bookings; the Hans calendar mirrors it. So nothing appears here that isn't in Scheduler, and a time is changed, cancelled or added there rather than here. Connect a scheduling business under Settings → Scheduling — services, hours, availability and payment settings all open in Scheduler.",
        note:
          "Google Calendar connections belong to Scheduler too. Hans no longer connects to Google Calendar directly.",
      },
      {
        id: "needs-review",
        title: 'A session marked "Needs review"',
        content:
          "Occasionally a Scheduler appointment can't be matched to a student or class on your roster — a new booking under a name Hans doesn't hold yet, for instance. It's shown rather than hidden, labelled \"Needs review\", and fixed in Scheduler by naming the right attendee or class. It can't be written up until it is: there's nobody to write against.",
      },
      {
        id: "bookings",
        title: "Taking bookings from new students",
        content:
          "Families book you in Scheduler, on the booking page your scheduling business publishes. Approved bookings appear on your Hans calendar, and you attach the person to a student record when you're ready — a booker is never added to your roster on their own, and a booking is never proof of consent.",
      },
      {
        id: "connected-classes",
        title: "Classes families book individually",
        content:
          "If families book a class one seat at a time, connect the Scheduler service to a named Hans class under Settings → Scheduling. Each meeting then appears once in your day rather than once per booking, and recording it produces the single shared summary a class should have.",
      },
    ],
  },
  {
    id: "programs",
    icon: Building2,
    title: "Programs & Organizations",
    sections: [
      {
        id: "programs-overview",
        title: "What the Programs tab holds",
        content: "Three tabs over one idea — the programs in your working life:",
        items: [
          { label: "My programs", desc: "What you teach in now, and what you used to" },
          { label: "Opportunities", desc: "Programs from organizations on Hans that you can apply to" },
          { label: "Invites", desc: "Invitations addressed to you, waiting for an answer" },
        ],
        media: shot(
          "programs.png",
          "The Programs page in Hans showing the programs an instructor teaches in and previous affiliations",
          "Programs"
        ),
      },
      {
        id: "joining",
        title: "Joining an organization",
        content:
          "An organization can't attach you to itself. It invites you, the invitation shows up under Invites (and as a banner in Settings), and you accept or decline. Accepting is what lets the organization see the lessons you teach in its programs — so it is always your decision, and never a side effect of being listed somewhere.",
        note: 'Want to be found? Turn on "Open to teaching work" in Settings → Profile. It tells you exactly what an organization can see before you do.',
      },
      {
        id: "teaching-in-a-program",
        title: "Teaching in a program",
        content:
          "Lessons you teach inside a program are attributed to it, and the organization sees those summaries — never your private students, and never your own notes. A program's classes and their meetings are set by the organization unless it has given the program's instructors permission to set their own times.",
      },
      {
        id: "applying",
        title: "Applying to a program",
        content:
          "Open an opportunity, read what it covers — instruments, location, days, dates — and apply with a note. The organization reviews applications and answers; if it accepts you, the program moves to My programs and its classes appear on your calendar.",
      },
      {
        id: "leaving",
        title: "Leaving an organization",
        content:
          "Leave from the organization's card on the Programs page. Its visibility into your teaching ends immediately and any policy it set on your account is cleared. Lessons already taught stay where they are — they are the organization's record as well as yours.",
      },
    ],
  },
  {
    id: "plan",
    icon: CreditCard,
    title: "Plan & Billing",
    sections: [
      {
        id: "plan-section",
        title: "What your account is on",
        content:
          "Settings → Plan & billing names your current plan, what it includes, and what you have left. Three things sit there together because they answer one question:",
        items: [
          { label: "Subscription", desc: "The plan you're on, when it renews, and how to change or cancel it" },
          { label: "Recording minutes", desc: "AI recording used and remaining, per plan and per organization" },
          { label: "Students", desc: "Your active roster against the plan's limit, if it has one" },
        ],
        media: shot(
          "plan-and-billing.png",
          "The Plan and billing section of Hans settings, showing the current plan, recording minutes, and active students",
          "Plan & billing"
        ),
        // The screenshot is the web purchase surface, so the app gets text only.
        ios: {
          content: "Settings in the app names your current plan, what it includes, and what you have left:",
          items: [
            { label: "Subscription", desc: "The plan you're on and when it renews" },
            { label: "Recording minutes", desc: "AI recording used and remaining, per plan and per organization" },
            { label: "Students", desc: "Your active roster against the plan's limit, if it has one" },
          ],
          media: null,
        },
      },
      {
        id: "more-minutes",
        title: "Running out of minutes",
        content:
          "If you are short before the month turns over, buy a one-off pack of minutes from the same card. Bought minutes don't expire and don't renew — they sit alongside the plan's allowance and are used after it. Changing plan is a separate decision, and you don't have to make it to finish the week.",
        ios: {
          content:
            "If you run short before the month turns over, open Settings in the app to see your options. You can always write a lesson up by hand, which never uses minutes.",
        },
      },
      {
        id: "covered-by-an-org",
        title: "When an organization covers you",
        content:
          "If an organization you have joined pays for Hans, your account reads as covered by it and the minutes you record in its programs are counted against the organization, not you. You can still hold your own subscription for your private students; Settings shows both.",
      },
      {
        id: "invoice-history",
        title: "Invoice history",
        content:
          "Invoice history lists the invoices raised for your students, with their state and date. It is a record rather than a tool: Hans no longer raises new tuition invoices, and the history stays so the ones already sent remain accounted for.",
      },
      {
        id: "referrals",
        title: "Sharing Hans",
        content:
          "Settings → Share Hans gives you a link to pass to another teacher. If they sign up through it, you both get credit — and if you joined in the founding cohort, the same card is where your renewal terms are shown.",
      },
    ],
  },
  {
    id: "student-portal",
    icon: GraduationCap,
    title: "Student Portal",
    sections: [
      {
        id: "portal-overview",
        title: "What your students see",
        content:
          "Students who accept an invitation get their own sign-in with their lesson summaries, the practice items you wrote for them, their own practice recordings, and a portfolio of their progress. Parents typically hold the account for younger students.",
      },
      {
        id: "portal-visibility",
        title: "When a lesson appears for them",
        content:
          "A lesson shows up in the student's portal once you've sent the summary — not before. Nothing you're still drafting is visible to them, so you can take your time over a write-up without the student reading a half-finished version.",
      },
      {
        id: "rehearsals",
        title: "Practice recordings",
        content:
          "Students can record themselves practising and send it to you. Their recordings appear on the related lesson page, where you can listen and leave written feedback that goes back to them.",
      },
      {
        id: "portal-privacy",
        title: "Privacy and access",
        content:
          "Students only ever see their own material. They can't see your other students, your roster, or your private notes, and they can't change anything about their lessons. You control access from their profile.",
      },
      {
        id: "student-guide-link",
        title: "A guide written for them",
        content:
          "Students and parents have their own version of this guide covering the portal alone — reading lessons, logging practice, recording a rehearsal, and the privacy that protects them. Point them at hans.kalver.ai/student-guide; it's linked from their Profile page too.",
      },
    ],
  },
  {
    id: "repertoire-tools",
    icon: BookOpen,
    title: "Repertoire & Tools",
    sections: [
      {
        id: "repertoire",
        title: "Repertoire assistant",
        content:
          'Type a piece — "Clair de Lune", "Autumn Leaves", a study from a method book — and Hans writes a teaching guide for it: where to start, the technique underneath it, the places students usually come unstuck, and a practice plan. Pick a student first and the guide is written for where they actually are.',
      },
      {
        id: "repertoire-library",
        title: "Your library",
        content:
          "Guides you save are kept under Repertoires and on the student's profile, so the plan you made in September is still there in March. Regenerate one any time.",
      },
      {
        id: "tools",
        title: "Metronome and tuner",
        content:
          "Tools has a metronome with tap tempo and common time signatures, and a chromatic tuner that works for any instrument through your device's microphone. Both are also available inside the recording screen, so reaching for one doesn't mean leaving the lesson.",
        media: shot("tools.png", "The Hans metronome and tuner tools", "Tools"),
      },
    ],
  },
  {
    id: "account",
    icon: Settings,
    title: "Account & Privacy",
    sections: [
      {
        id: "profile",
        title: "Your profile",
        content:
          "Settings holds your name, instruments, location, and experience, and whether organizations on Hans may find you and invite you to their programs.",
      },
      {
        id: "notifications",
        title: "Notifications",
        content:
          "Choose what you want to hear about — a summary being ready, a student claiming their portal invite, and whether you're copied on the summaries you send. Notifications reach you by email, and by push as well if you use the iOS app. Every email also has a one-click unsubscribe if you'd rather it stopped.",
      },
      {
        id: "appearance",
        title: "Light or dark",
        content:
          "Settings → Account → Appearance switches between them. Light is the default and dark suits a dim room; the choice is saved on the device you made it on, so your laptop and your phone are independent.",
      },
      {
        id: "data-privacy",
        title: "How your data is handled",
        content:
          "Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days. The written summary is kept, and it isn't used to train anything. Everything is encrypted in transit and at rest, and none of it is sold or used for advertising. If a family withdraws consent, remaining audio and transcripts for that student are deleted too.",
      },
      {
        id: "organizations",
        title: "If you teach for an organization",
        content:
          "An organization that seats you can see the lessons you teach in its programs — never your private students, and never your notes. The membership, and the way out of it, live on the Programs page.",
      },
    ],
  },
  {
    id: "troubleshooting",
    icon: HelpCircle,
    title: "Troubleshooting & FAQ",
    sections: [
      {
        id: "faq-consent-blocked",
        title: "Hans won't let me record a student",
        content:
          'Recording is locked until that student has consented. The card on the recording screen says where consent stands; send or resend the request from the student\'s profile. If the family signed on paper, use "Mark as consented (offline)" on their profile instead. In the meantime you can still write the lesson up with "Write notes instead".',
      },
      {
        id: "faq-processing",
        title: "My recording is still processing",
        content:
          "Most lessons are ready within a few minutes; a long one takes longer. If nothing has appeared after about ten minutes, refresh the page. If it's still stuck, get in touch and we'll look at what happened to that specific lesson.",
      },
      {
        id: "faq-out-of-minutes",
        title: "Hans says I'm out of recording minutes",
        content:
          "Settings → Plan & billing shows what's left and when it renews. You can buy a one-off pack of minutes there without changing plan, and you can always write a lesson up by hand with \"Write notes instead\" — that never costs minutes.",
        ios: {
          content:
            "Settings in the app shows what's left and when it renews. You can always write a lesson up by hand, which never costs minutes.",
        },
      },
      {
        id: "faq-lesson-missing",
        title: "A lesson isn't on my calendar",
        content:
          "The calendar mirrors Kalver Scheduler, so a lesson that isn't in Scheduler can't appear here. Check it in Scheduler first; if it is there and Hans hasn't caught up, the calendar shows when it last synced and any connection that failed to answer.",
      },
      {
        id: "faq-iphone-stopped",
        title: "My recording stopped partway through on my phone",
        content:
          "On an iPhone or iPad, recording in a browser ends as soon as you leave the tab or the screen locks — iOS takes the microphone away. Whatever was captured before that is kept and can be uploaded. If you teach from your phone, use the Hans app, which records in the background.",
      },
      {
        id: "faq-mic",
        title: "The recorder or tuner can't hear anything",
        content:
          'Check that you allowed microphone access — look for the microphone icon in your browser\'s address bar and set it to "Allow". On iOS, check Settings → Safari → Microphone. If another app is holding the microphone, close it and reload the page.',
      },
      {
        id: "faq-summary-quality",
        title: "A summary came out thin or wrong",
        content:
          "Summary quality follows audio quality. Background noise, distance from the device, and people talking over each other all cost detail. You can edit anything the summary got wrong before sending, and re-record the lesson if the audio was the problem. If a summary looks wrong in a way audio doesn't explain, tell us.",
      },
      {
        id: "faq-student-invite",
        title: "My student didn't get their portal invite",
        content:
          "Check the email address on their profile first, then ask them to look in spam. You can resend from the profile at any time. A student who already has a portal account with another teacher signs in with their existing password rather than creating a new one.",
      },
      {
        id: "faq-consent-email",
        title: "The parent says the consent email never arrived",
        content:
          "Check the parent's email address on the student's profile — for minors the request goes to the parent, not the student. Ask them to check spam, then resend. Each resend creates a fresh link and the old one stops working.",
      },
      {
        id: "faq-contact",
        title: "How do I get help?",
        content: `Email ${supportEmail()} and a person will answer. You can also send feature requests and bug reports from the Feedback page — we do read them.`,
      },
    ],
  },
];

const GuidePage = () => <GuideView categories={guideCategories} />;

export default GuidePage;
